import {isBefore, startOfDay} from 'date-fns';
import {isFeatureEnabled} from 'feature-flags';
import {closeCurrentDialog, openDialog, showNotification} from 'platform/components';
import {useDateTimeFormatter, useLocale} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {
  useGetBusinessCaseQuery,
  useGetOfferAlternativesQuery,
  useGetVehicleQuery,
  useLazyGetBusinessCaseQuery,
  useSendBusinessCaseOnlineOfferMutation,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {parseDate} from 'shared/web';

import {InvalidateOnlineOfferDialog} from '../components/InvalidateOnlineOfferDialog/InvalidateOnlineOfferDialog';
import {OfferValidityDialog} from '../components/OfferValidityDialog/OfferValidityDialog';
import {SendCombinedOnlineOfferDialog} from '../components/SendCombinedOnlineOfferDialog/SendCombinedOnlineOfferDialog';
import {SendOnlineOfferEmailForm} from '../components/SendOnlineOfferEmailForm/SendOnlineOfferEmailForm';
import {usePermissions} from '../hooks/usePermissions/usePermissions';
import {getBuyingOnlineOfferUri} from '../utils/getBuyingOnlineOfferUri';
import {getOnlineOfferUri} from '../utils/getOnlineOfferUri';
import {handleApiError} from '../utils/handleApiError';
import {shouldOfferCombinedOnlineOffer} from './utils/shouldOfferCombinedOnlineOffer';

// eslint-disable-next-line complexity -- Pre-existing violation
export const useBcToOnlineOffer = (businessCaseId: string) => {
  const {language} = useLocale();
  const formatDateTime = useDateTimeFormatter();

  const {data: businessCaseData} = useGetBusinessCaseQuery({businessCaseId});
  const linkedTradeInBusinessCaseId = businessCaseData?.linkedTradeInBusinessCaseId;
  const {data: linkedTradeInBusinessCaseData} = useGetBusinessCaseQuery(
    {businessCaseId: linkedTradeInBusinessCaseId ?? ''},
    {skip: isNilOrEmpty(linkedTradeInBusinessCaseId)}
  );
  const [getBusinessCase, {isLoading: isGetBusinessCaseLazyQueryLoading}] =
    useLazyGetBusinessCaseQuery();

  const dealVehicleId = businessCaseData?.offers[0]?.saleVehicles?.[0]?.vehicleId;
  // Offer alternatives (T20-139208): a Business Case can offer alternatives before any deal
  // vehicle exists. Only then is the online offer sent without a vehicle; with a deal vehicle, or
  // without alternatives, everything below behaves exactly as before.
  const isOfferAlternativesEnabled = isFeatureEnabled(featureFlags.NEW_VEHICLE_CATALOGUE);
  const isConcept = businessCaseData?.businessCaseState === 'CONCEPT';
  const {data: offerAlternatives} = useGetOfferAlternativesQuery(
    {businessCaseId},
    {skip: !isOfferAlternativesEnabled || (isNotNilOrEmpty(dealVehicleId) && !isConcept)}
  );
  const hasAlternatives =
    isOfferAlternativesEnabled && (offerAlternatives?.alternatives.length ?? 0) > 0;
  const canSendWithoutVehicle = hasAlternatives && isNilOrEmpty(dealVehicleId);

  // A CONCEPT Business Case that offers alternatives already has an online offer to share.
  const isConceptWithAlternatives = hasAlternatives && isConcept;

  const {data: vehicleData} = useGetVehicleQuery(
    {vehicleId: businessCaseData?.offers[0]?.saleVehicles?.[0]?.vehicleId ?? ''},
    {skip: isNilOrEmpty(businessCaseData?.offers[0]?.saleVehicles?.[0]?.vehicleId)}
  );

  const {data: participation} = useGetParticipationQuery({
    recordId: businessCaseId,
    resourceId: EntityResourceIds.businessCase,
  });
  const {data: linkedTradeInParticipation} = useGetParticipationQuery(
    {
      recordId: linkedTradeInBusinessCaseId ?? '',
      resourceId: EntityResourceIds.businessCase,
    },
    {skip: isNilOrEmpty(linkedTradeInBusinessCaseId)}
  );

  const {data: ownerInfo} = useGetUserQuery(
    {id: participation?.owner?.userId || ''},
    {skip: isNilOrEmpty(participation?.owner?.userId)}
  );

  const [createOnlineOffer, {isLoading: isCreateOnlineOfferMutationLoading}] =
    useSendBusinessCaseOnlineOfferMutation();

  const [
    canSendOnlineOffer,
    canInvalidateOnlineOffer,
    canReadOnlineOffer,
    canSendBuyingOnlineOffer,
  ] = usePermissions({
    permissionKeys: [
      'sendOnlineOffer',
      'invalidateOnlineOffer',
      'readOnlineOffer',
      'sendBuyingOnlineOffer',
    ],
    scopes: {
      sendOnlineOffer: {
        participation,
        branchId: businessCaseData?.branchId,
      },
      invalidateOnlineOffer: {
        participation,
        branchId: businessCaseData?.branchId,
      },
      readOnlineOffer: {
        participation,
        branchId: businessCaseData?.branchId,
      },
      sendBuyingOnlineOffer: {
        participation: linkedTradeInParticipation,
        branchId: linkedTradeInBusinessCaseData?.branchId,
      },
    },
  });

  const isBusinessCaseInCorrectState =
    (businessCaseData?.businessCaseState !== 'CONCEPT' &&
      businessCaseData?.businessCaseState !== 'UNSUCCESSFUL') ||
    isConceptWithAlternatives;

  const canSendOffer =
    isBusinessCaseInCorrectState &&
    canSendOnlineOffer &&
    isNilOrEmpty(businessCaseData?.onlineOfferFirstSentAt);

  const isOnlineOfferCreated = isNotNilOrEmpty(businessCaseData?.offerId);

  const isOnlineOfferSent = isNotNilOrEmpty(businessCaseData?.onlineOfferFirstSentAt);

  const canOpenOnlineOffer = canSendOnlineOffer && isBusinessCaseInCorrectState;

  const canResendOffer = isBusinessCaseInCorrectState && canSendOnlineOffer && isOnlineOfferSent;

  const canInvalidateOffer = isOnlineOfferCreated && canInvalidateOnlineOffer && isOnlineOfferSent;

  const canSendCombinedOnlineOffer = shouldOfferCombinedOnlineOffer({
    businessCaseInternalType: businessCaseData?.businessCaseInternalType,
    buyingVehicleId: linkedTradeInBusinessCaseData?.offers[0]?.purchaseVehicles?.[0]?.vehicleId,
    linkedTradeInBusinessCaseId: businessCaseData?.linkedTradeInBusinessCaseId,
    canSendBuyingOnlineOffer,
  });

  const formattedOfferValidTo = businessCaseData?.offerValidTo
    ? formatDateTime('dateShort', parseDate(businessCaseData?.offerValidTo))
    : undefined;

  const isOfferExpired = businessCaseData?.offerValidTo
    ? isBefore(parseDate(businessCaseData.offerValidTo), startOfDay(new Date()))
    : false;

  const openSendOnlineOfferDialog = ({
    shouldCreateOffer = false,
    onSuccess,
    onlineOfferKind = 'SELLING',
  }: {
    shouldCreateOffer?: boolean;
    onSuccess?: () => void;
    onlineOfferKind?: 'SELLING' | 'COMBINED';
  } = {}) => {
    if (
      businessCaseData &&
      participation?.owner?.userId &&
      (vehicleData || canSendWithoutVehicle) &&
      ownerInfo?.email
    ) {
      const coverPhotoOfVehicle = vehicleData?.photos?.find((photo) => photo.isCoverPhoto);
      const buyingVehicleId =
        linkedTradeInBusinessCaseData?.offers[0]?.purchaseVehicles?.[0]?.vehicleId;
      const buyingOfferId = linkedTradeInBusinessCaseData?.offerId;
      const onlineOfferProps = match({onlineOfferKind, shouldCreateOffer})
        .with({onlineOfferKind: 'COMBINED'}, () => {
          if (!isNotNil(buyingVehicleId) || isNil(vehicleData)) {
            return null;
          }

          return {
            onlineOfferKind: 'COMBINED' as const,
            vehicleId: vehicleData.id,
            buyingVehicleId,
            sellingOnlineOfferUri: isNotNilOrEmpty(businessCaseData.offerId)
              ? getOnlineOfferUri({
                  offerId: businessCaseData.offerId,
                  language,
                  sellerEmail: ownerInfo.email,
                })
              : null,
            buyingOnlineOfferUri: isNotNilOrEmpty(buyingOfferId)
              ? getBuyingOnlineOfferUri({
                  offerId: buyingOfferId,
                  language,
                  sellerEmail: ownerInfo.email,
                })
              : null,
          };
        })
        .with({shouldCreateOffer: true}, () => ({
          onlineOfferKind: 'SELLING' as const,
          vehicleId: vehicleData?.id ?? null,
          sellerEmail: ownerInfo.email,
          shouldCreateOffer: true as const,
        }))
        .otherwise(() => ({
          onlineOfferKind: 'SELLING' as const,
          vehicleId: vehicleData?.id ?? null,
          onlineOfferUri: getOnlineOfferUri({
            offerId: businessCaseData.offerId,
            language,
            sellerEmail: ownerInfo.email,
          }),
          shouldCreateOffer: false as const,
        }));

      if (isNil(onlineOfferProps)) {
        return;
      }

      openDialog(
        <SendOnlineOfferEmailForm
          {...onlineOfferProps}
          businessCaseData={businessCaseData}
          sellerId={participation.owner.userId}
          photoId={coverPhotoOfVehicle?.fileId}
          onSuccess={onSuccess}
        />,
        {
          title: i18n.t('entity.sendEmailOnlineOfferDialog.heading'),
          id: 'sendEmailOnlineOfferDialog',
          'data-testid': 'sendEmailOnlineOfferDialog',
          scrollBehavior: 'inside',
          size: 'large',
          disableMaxHeight: true,
        }
      );
    }
  };

  const sendOnlineOfferToCustomer = ({
    shouldCreateOffer = false,
    onSuccess,
  }: {
    shouldCreateOffer?: boolean;
    onSuccess?: () => void;
  } = {}) => {
    // The combined offer pairs the deal vehicle with the trade-in; without one, sell only.
    if (!canSendCombinedOnlineOffer || (canSendWithoutVehicle && isNil(vehicleData))) {
      openSendOnlineOfferDialog({shouldCreateOffer, onSuccess});
      return;
    }

    openDialog(
      <SendCombinedOnlineOfferDialog
        onSendBothOffers={() => {
          closeCurrentDialog();
          openSendOnlineOfferDialog({onSuccess, onlineOfferKind: 'COMBINED'});
        }}
        onSendSellingOffer={() => {
          closeCurrentDialog();
          openSendOnlineOfferDialog({shouldCreateOffer, onSuccess});
        }}
      />,
      {
        title: i18n.t('entity.sendCombinedOnlineOfferDialog.heading'),
        id: 'sendCombinedOnlineOfferDialog',
        'data-testid': 'sendCombinedOnlineOfferDialog',
      }
    );
  };

  const invalidateOnlineOfferToCustomer = () => {
    if (businessCaseData?.offerId) {
      openDialog(<InvalidateOnlineOfferDialog offerId={businessCaseData.offerId} />, {
        title: i18n.t('entity.invalidateOnlineOfferDialog.title'),
        id: 'invalidateOnlineOfferDialog',
        'data-testid': 'invalidateOnlineOfferDialog',
      });
    }
  };

  const openOffer = async () => {
    let offerId = businessCaseData?.offerId;

    if (!isOnlineOfferCreated) {
      await createOnlineOffer({businessCaseId}).unwrap().catch(handleApiError);
      const newBcData = await getBusinessCase({businessCaseId}).unwrap();
      if (!newBcData.offerId) {
        showNotification.error();
        return;
      }
      offerId = newBcData.offerId;
    }

    const onlineOfferUrl = getOnlineOfferUri({
      offerId: offerId ?? businessCaseData?.offerId,
      language,
      sellerEmail: ownerInfo?.email,
      includeOpenedBy: false,
    });
    window.open(onlineOfferUrl, '_blank')?.focus();
  };

  const openOfferValidityDialog = () => {
    openDialog(
      <OfferValidityDialog
        currentOfferValidTo={businessCaseData?.offerValidTo}
        businessCaseId={businessCaseId}
      />,
      {
        title: i18n.t('entity.offerValidityDialog.title'),
        id: 'offerValidityDialog',
        'data-testid': 'offerValidityDialog',
        size: 'small',
      }
    );
  };

  return {
    canSendOffer,
    canResendOffer,
    canInvalidateOffer,
    isOnlineOfferCreated,
    isOnlineOfferSent,
    canOpenOnlineOffer,
    canReadOnlineOffer,
    offerValidity: {
      validTo: formattedOfferValidTo,
      isExpired: isOfferExpired,
      openDialog: openOfferValidityDialog,
    },
    isOpenOnlineOfferLoading:
      isGetBusinessCaseLazyQueryLoading || isCreateOnlineOfferMutationLoading,
    actions: {
      sendOnlineOfferToCustomer,
      invalidateOnlineOfferToCustomer,
      openOffer,
    },
  };
};
